<template>
  <div class="chatbot-container">
    <div class="chatbot-header">
      <button
        class="btn btn-icon btn-secondary sidebar-toggle-btn pull-left"
        @click="toggleSidebar"
      >
        <i :class="isSidebarOpen ? 'fa fa-chevron-right' : 'fa fa-chevron-left'" aria-hidden="true"></i>
      </button>
      <button class="btn btn-icon btn-secondary" @click="newChat()">
        <i class="fa fa-plus mr-0-2" aria-hidden="true"></i>{{ $t('new_chat') }}
      </button>
    </div>
    <PerfectScrollbar class="ps-container" ref="chatContent" :settings="settings" @ps-scroll-y="scrollHandle" name="chatContent">
      <div class="chatbot-messages" ref="messagesContainer">
      <div v-for="(message, index) in currentChatThread.content" :key="index" :class="['message', message.type]"  @mouseleave="hideCopyButton(index)">
        <div v-if="message.role === 'user'" class="chats">
          <div class="chat-avatar">
            <img :src="avatar" class="rounded-circle dreams_chat" :alt="userName">
          </div>
          <div class="user-message" v-html="message.content">
          </div>
        </div>
        <div v-else class="chats">
          <div>
            <div class="bot-message" @mouseover="showCopyButton($event, index)">
              <div v-html="message.content"></div>
            </div>
            <div v-if="message.sources && message.sources.length && !isLoading" class="sources-wrapper">
              <div class="source-header" @click="toggleSources(index)">
                <strong>Quellen ({{ message.sources.length }}):</strong>
                <i :class="['fa', expandedSources[index] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
              </div>

              <div v-if="expandedSources[index]" class="source-list-container">
                <div v-for="(source, sIndex) in message.sources" :key="sIndex" class="source-item">

                  <div class="source-item-title" @click="toggleSourceDetail(index, sIndex)">

                    <template v-if="source.source_type === 'book'">
                      <i class="fa fa-book" style="margin-right: 8px; color: #555;"></i>
                    </template>
                    <template v-else>
                      <img :src="getFavicon(source.url)" class="favicon" alt="icon" @error="handleIconError">
                    </template>

                    <span class="source-name">{{ source.title || source.name || 'Unbekannte Quelle' }}</span>
                  </div>

                  <transition name="collapse">
                    <div v-if="expandedDetails[index + '-' + sIndex]" class="source-detail">

                      <p v-if="source.content" class="source-snippet">
                        {{ source.content }}
                      </p>

                      <a v-if="source.source_type !== 'book' && source.url" :href="source.url" target="_blank" class="source-link">
                        <i class="fa fa-external-link"></i> {{ source.url }}
                      </a>

                      <div v-if="source.source_type === 'book'" class="book-badge">
                        <small style="color: #888;">local library</small>
                      </div>

                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
          <div class="chat-avatar">
            <img :src="chatgpt_icon" class="rounded-circle dreams_chat" alt="image">
          </div>
        </div>
        <button v-if="copyButtonVisible[index]" @click="copyToClipboard($event, message.content)" class="btn btn-light copy-button" :style="copyBtnStyle">copieren</button>
      </div>
      <div v-show="isLoading" class="chats pull-right">
        <div>
          <span class="animate-typing-col">Thinking
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </span>
        </div>
        <div class="chat-avatar">
          <img :src="chatgpt_icon" class="rounded-circle dreams_chat" alt="image">
        </div>
      </div>
      <div v-if="alertVisible" class="copy-message copy-alert" :style="alertStyle">{{ alertMessage }}</div>
    </div>
    </PerfectScrollbar>
    <div class="chatbot-input-area">
      <!-- TOP ROW: prompt + toggles + attachment (OUTSIDE input-container) -->
      <div class="top-controls-row">
        <!-- Prompt choice -->
        <div class="prompt-choice">
          <select class="select select-single" v-model="selectedPrompt" v-select2>
            <option
              v-for="choice in promptChoices"
              :key="choice.id"
              :value="choice.id"
            >
              {{ choice.name }}
            </option>
          </select>
        </div>

        <!-- Toggle buttons -->
        <div class="switches-container switches-row">

          <!-- Attachment trigger button -->
          <button
            @click="showAttachmentDropdown"
            class="attachment-button rounded-pill"
          >
            <i class="fa fa-paperclip"></i>
          </button>
          <button
            class="toggle-button"
            :class="{ active: anonym }"
            @click="anonym = !anonym"
            title="Die personbezogene Daten werden geprüft vor dem Senden"
          >
            <i class="fa fa-user-secret"></i> <span class="button-text">Anonym</span>
          </button>

          <button
            class="toggle-button"
            :class="{ active: reasoning }"
            @click="reasoning = !reasoning"
            title="Zwingt die KI, ihre Gedankengänge zu erklären"
          >
            <i class="fa fa-brain"></i> <span class="button-text">Frage spezifizieren</span>
          </button>

          <button
            class="toggle-button"
            :class="{ active: searchInternet }"
            @click="searchInternet = !searchInternet"
            title="Sucht im Live-Internet nach aktuellen Informationen"
          >
            <i class="fa fa-globe"></i> <span class="button-text">Such Internet</span>
          </button>

          <button
            class="toggle-button"
            :class="{ active: checkBooks }"
            @click="checkBooks = !checkBooks"
            title="Durchsucht die lokale Fachliteratur-Datenbank"
          >
            <i class="fa fa-book"></i> <span class="button-text">Such in Fachliteraturen</span>
          </button>

          <button
            class="toggle-button"
            :class="{ active: deepSearch }"
            @click="deepSearch = !deepSearch"
            title="Aktiviert Themen Forschung für maximale Tiefe"
          >
            <i class="fa fa-search-plus"></i> <span class="button-text">Deep Search</span>
          </button>

          <button
            class="toggle-button"
            :class="{ active: debate }"
            @click="debate = !debate"
            title="Lässt 5 KI-Experten das Thema kritisch diskutieren"
          >
            <i class="fa-solid fa-users-between-lines"></i> <span class="button-text">Umfangreiches Prüfen</span>
          </button>

          <button
            class="toggle-button"
            :class="{ active: generateImage }"
            @click="generateImage = !generateImage"
            title="Erzeugt ein passendes Bild zum Text"
          >
            <i class="fa fa-image"></i> <span class="button-text">Info-Bild</span>
          </button>
        </div>
      </div>

      <!-- PURPLE BORDER AREA: file list + textarea + send button -->
      <div class="input-container input-main-wrapper file-list-container">
        <div class="input-main-left">
          <!-- File list above textarea -->
          <div v-if="summaryFiles && summaryFiles.length" class="file-list">
            <div
              v-for="(file, index) in summaryFiles"
              :key="file.id"
              class="file-item"
            >
              <span>{{ file.name }}</span>
              <span class="remove" @click="removeFile(index)">×</span>
            </div>
          </div>

          <!-- Textarea full width inside border -->
          <textarea
            v-model="userInput"
            @keyup.enter="sendMessage"
            placeholder="Schreib was..."
            rows="5"
          />
        </div>

        <!-- Send button on the right -->
        <button
          @click="sendMessage"
          :disabled="isLoading"
          class="send-button rounded-pill"
        >
          <i v-if="isLoading" class="fa fa-cog fa-w-16 fa-spin fa-2x"></i>
          <span v-else><i class="fa fa-paper-plane"></i></span>
        </button>
      </div>

      <!-- Hidden file input + dropdown (position as before) -->
      <input
        type="file"
        ref="fileInput"
        @change="handleFileUpload"
        style="display: none"
        multiple="multiple"
        :accept="allowedExtensions"
      />

      <div
        v-if="showDropdown"
        class="attachment-dropdown"
        :style="dropdownStyle"
      >
        <div class="popup-content">
          <button
            class="btn btn-icon btn-outline-info"
            @click="triggerFileUpload('word')"
          >
            <i class="fa-regular fa-file-word mr-0-2" aria-hidden="true"></i>Word
          </button>
          <button
            class="btn btn-icon btn-outline-warning"
            @click="triggerFileUpload('pdf')"
          >
            <i class="fa-regular fa-file-pdf mr-0-2" aria-hidden="true"></i>PDF
          </button>
          <button
            class="btn btn-icon btn-outline-primary"
            @click="triggerFileUpload('mp3')"
          >
            <i class="fa-regular fa-file-audio mr-0-2" aria-hidden="true"></i>Audio
          </button>
          <button
            class="btn btn-icon btn-outline-secondary"
            @click="triggerFileUpload('mp4')"
          >
            <i class="fa-regular fa-file-video mr-0-2" aria-hidden="true"></i>Video
          </button>
          <button
            class="btn btn-icon btn-outline-danger"
            @click="triggerFileUpload('youtube')"
          >
            <i class="fa-brands fa-youtube mr-0-2" aria-hidden="true"></i>YouTube Link
          </button>
        </div>
      </div>
    </div>
    <div v-if="showYoutubeInput" class="youtube-url-popup">
      <div class="input-group">
        <input
          type="text"
          v-model="tempYoutubeURL"
          class="form-control"
          placeholder="YouTube URL hier einfügen..."
          @keyup.enter="confirmYoutubeUrl"
        >
        <div class="input-group-append">
          <button class="btn btn-primary" @click="confirmYoutubeUrl">Hinzufügen</button>
          <button class="btn btn-secondary" @click="showYoutubeInput = false">Abbrechen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import localforage from "localforage";
import markdownit from 'markdown-it';
import { PerfectScrollbar } from "vue2-perfect-scrollbar";
import "vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css";
import {mapState} from "vuex";
import {service, baseURL} from "@/utils/request";
import axios from "axios";
import {formatDate, initSelect2, summarizeMessages} from "@/utils";

export default {
  name: "AIChatComponent",
  components: {
    PerfectScrollbar,
  },
  props: {
    currentChatThread: {
      type: Object,
      default: {chat_date: '', title: '', content: [], send_messages: []},
    },
    isSidebarOpen: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      userInput: "", // User input from the chat box
      chatMessages: [], // Messages to display in the chat
      messages: [], // Messages to display in the chat
      promptChoices: [],
      prompts:{},
      selectedPrompt: null,
      settings: {
        suppressScrollX: true,
      },
      isLoading: false,
      copyButtonVisible: {},
      maxChatMessages: 10,
      maxChatWords: 10000,
      alertVisible: false,
      alertMessage: '',
      alertStyle: {},
      anonym: true,
      reasoning: false,
      checkBooks: false,
      deepSearch: false,
      searchInternet: false,
      debate: false,
      generateImage: false,
      chatgpt_icon: require("@/assets/img/gpt.png"),
      fileFormat: 'word',
      allowedExtensions: '.doc, .docx',
      showDropdown: false,
      fileSummary: false,
      summaryFiles: [],
      currentTaskId: '',
      youtubeURL: '',
      showYoutubeInput: false,
      tempYoutubeURL: '',
      fileId: 0,
      taskResult: '',
      dropdownStyle: {
        left: '0px',
        top: '0px',
      },
      expandedSources: {}, // Tracks which message's source list is open
      expandedDetails: {}, // Tracks which specific source detail is open (key: msgIndex-sourceIndex)
    };
  },
  computed: {
    ...mapState({accountid: state => state.account.accountid, userName: state => state.user.username,
      account: state => state.account, user: state => state.user, language: state => state.app.language,
      token: state => state.user.token, avatar: state => state.user.avatar}),
  },
  watch: {
    selectedPrompt(newVal) {
      const promptObj = this.promptChoices.find(choice => choice.id === newVal);
      this.fileSummary = promptObj.name === 'Zusammenfassung von Artikel';
    },
    deepSearch(newVal) {
      if (newVal) {
        this.searchInternet = true;
        this.checkBooks = true;
      }
    }
  },
  async mounted() {
    initSelect2()
    this.initChatDb();
    this.emptyChatContent();
    await this.getPrompts();
  },
  methods: {
    showAttachmentDropdown(event) {
      event.preventDefault();
      this.showDropdown=!this.showDropdown;
      let leftPos = event.clientX - 320;
      if (leftPos <= 0) leftPos = event.clientX;
      let topPos = event.clientY - 50;
      if (topPos <= 0) topPos = event.clientY;
      this.dropdownStyle = {
        left: leftPos + 'px',
        top: topPos + 'px',
      }
    },
    removeFile(index) {
      this.summaryFiles.splice(index, 1);
    },
    triggerFileUpload(formatName) {
      if (formatName === 'youtube') {
        this.showYoutubeInput = true;
        this.showDropdown = false;
        return;
      }

      if (formatName === 'word') {
        this.allowedExtensions = '.doc, .docx'
        this.fileFormat = 'word'
      } else if (formatName === 'pdf') {
        this.allowedExtensions = '.pdf'
        this.fileFormat = 'pdf'
      } else if (formatName === 'mp3') {
        this.allowedExtensions = '.mp3, .m4a, .wav, .aac'
        this.fileFormat = 'mp3'
      } else {
        this.allowedExtensions = '.mp4, .webm, .avi, .mpeg, .mkv'
        this.fileFormat = 'mp4'
      }
      this.$nextTick(() => {
        this.$refs.fileInput.click();
        this.showDropdown = false
      });
    },
    // New method to handle the YouTube URL confirmation
    confirmYoutubeUrl() {
      if (this.tempYoutubeURL.trim()) {
        this.youtubeURL = this.tempYoutubeURL.trim();
        // Optionally add it to the summaryFiles list visually so user sees it's attached
        this.summaryFiles.push({
          id: 'yt-' + Date.now(),
          name: `YouTube: ${this.youtubeURL}`,
          type: 'youtube',
          url: this.youtubeURL
        });
        this.tempYoutubeURL = '';
        this.showYoutubeInput = false;
      }
    },
    async handleFileUpload(e) {
      for (let i = 0; i< e.target.files.length; i++) {
        const file = e.target.files[i]
        if (file) {
          // Prevent duplicate files by name and size
          const exists = this.summaryFiles.some(
              f => f.name === file.name && f.size === file.size
          );
          if (!exists) {
            this.summaryFiles.push({
              id: this.fileId++,
              name: file.name,
              file: file,
              size: file.size,
            });
          }
        }
      }
    },
    async getPrompts() {
      service.request({
        url: `/todos/v1/prompts/`,
        method: 'GET',
      }).then((response) => {
        this.prompts = response.data.results
        this.promptChoices = []
        this.prompts.forEach(prompt => this.promptChoices.push({id: prompt.id, name: prompt.name}))
      })
    },
    showCopyButton(event, index) {
      this.copyBtnStyle = {
        top: `${event.clientY - 50}px`,
        left: `${event.clientX}px`
      };
      this.$set(this.copyButtonVisible, index, true);
    },
    hideCopyButton(index) {
      this.$set(this.copyButtonVisible, index, false);
    },
    copyToClipboard(event, text) {
      // Create a temporary element to parse HTML properly
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      const plain_text = tempDiv.innerText || tempDiv.textContent;

      // Create ClipboardItem with both formats
      const htmlBlob = new Blob([text], { type: 'text/html' });
      const textBlob = new Blob([plain_text], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      });
      navigator.clipboard.write([clipboardItem]).then(() => {
        this.showAlert(event, 'erfolgreich kopiert!')
      }).catch(err => {
        this.showAlert(event, `Fehler beim Kopieren: ${err}`)
      });
    },
    showAlert(event, alertText) {
      this.alertMessage = alertText;
      this.alertStyle = {
        top: `${event.clientY}px`,
        left: `${event.clientX}px`
      };
      this.alertVisible = true;
      setTimeout(() => {
        this.alertVisible = false;
      }, 2000);  // Fade away after 2 seconds
    },
    // Initialize LocalForage instance
    initChatDb() {
      this.chatDb = localforage.createInstance({
        name: "chatHistory",
      });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        // Access the component ref
        const scrollComponent = this.$refs.chatContent;
        if (!scrollComponent) return;

        // Access the root DOM element of the PerfectScrollbar component
        const container = scrollComponent.$el || scrollComponent;

        // Use a small timeout to ensure DOM paint is complete (especially for images/markdown)
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;

          // Force PerfectScrollbar to recalculate metrics
          if (scrollComponent.update) {
            scrollComponent.update();
          }
        }, 50);
      });
    },
    // Add a message to the chat
    addMessage(message, isUser = true) {
      const newMessage = {
        content: isUser ? `${this.$t('you')}: ${message}` : message,
        isUser,
      };
      this.chatMessages.push(newMessage);

      // Scroll to the bottom of the chat
      this.$nextTick(() => {
        this.$refs.chatContent.$el.scrollTop = this.$refs.chatContent.$el.scrollHeight;
      });
    },

    // Format a date string
    formatDate(dateString) {
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      }
    },

    // Save the current chat to LocalForage
    saveCurrentChat() {
      if (this.chatHistory.length === 0) return;
      const date = new Date();
      const title = this.chatHistory[0].user.slice(0, 50);
      this.chatDb
        .setItem(title, { date, content: this.chatHistory })
        .catch((err) => {
          console.error("Error saving data:", err);
        });
    },

    // Format a date for display
    getDateFormatted(date) {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const month = months[date.getMonth()];
      const day = date.getDate();
      return `${month} ${day}`;
    },

    // Clear the chat content
    emptyChatContent(date = null) {
      const today = date || new Date();
      this.chatMessages = [
        {
          content: `${this.$t('today')} ${this.getDateFormatted(today)}`,
          isUser: false,
        },
      ];
      this.chatTodayGrouped = {chat_date: this.getDateFormatted(today), content:{}}
    },

    // Start a new chat
    newChat() {
      this.$emit('newChat')
    },
    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    sanitizeContent(content) {
      // Simple sanitization to prevent XSS. For more complex sanitization, use libraries like DOMPurify.
      const div = document.createElement('div');
      div.textContent = content;
      return div.innerHTML;
    },
    // Send with multiple files using FormData
    async sendWithFiles() {
      const formData = new FormData();
      let fileCount = 0;
      // Append multiple files
      this.summaryFiles.forEach((file, index) => {
        if (file.type !== 'youtube') {
          formData.append(`files`, file.file);
          fileCount++;
        } // Django will receive as list
        // Alternative: formData.append(`file_${index}`, file); // Individual file names
      });

      // Append other parameters
      formData.append('check_pii', this.anonym);
      formData.append('language', this.language);
      formData.append('send_messages', this.currentChatThread.send_messages);

      // Optional: Add file count for backend validation
      formData.append('file_count', fileCount);

      return await axios.post(`${baseURL}/todos/v1/chat_document_summary/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': 'Token ' + this.token,
        },
        // Optional: Track upload progress
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload Progress: ${percentCompleted}%`);
        },
      });
    },
    async sendContextFiles() {
      const formData = new FormData();
      let fileCount = 0;
      formData.append('chat_id', this.currentChatThread.id);

      // Append multiple files
      this.summaryFiles.forEach((file, index) => {
        if (file.type !== 'youtube') {
          formData.append(`files`, file.file);
          fileCount++;
        } // Django will receive as list
        // Alternative: formData.append(`file_${index}`, file); // Individual file names
      });
      // Optional: Add file count for backend validation
      formData.append('file_count', fileCount);
      formData.append('youtube_url', this.youtubeURL);

      return await axios.post(`${baseURL}/todos/v1/chat_upload_context/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': 'Token ' + this.token,
        },
        // Optional: Track upload progress
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload Progress: ${percentCompleted}%`);
        },
      });
    },
    async pollTaskStatus(taskID) {
      if (!taskID) return { content: "Error: No Task ID", sources: [] };

      let totalTime = 0;
      const pollInterval = 5000; // 5 seconds
      const maxTimeout = 1800000; // 30 minutes

      return new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            if (totalTime >= maxTimeout) {
              clearInterval(interval);
              reject(new Error("Task timed out after 30 minutes"));
              return;
            }

            totalTime += pollInterval;

            const response = await axios.get(`${baseURL}/todos/v1/get_task_status/${taskID}/`, {
              headers: { 'Authorization': 'Token ' + this.token },
            });

            const { task_id, status, result } = response.data;

            if (status === 'SUCCESS') {
              clearInterval(interval);
              // Standardize the result structure
              const output = {
                content: typeof result === 'object' ? result.content : result,
                sources: result.sources || []
              };
              resolve(output);
            } else if (status === 'FAILURE') {
              clearInterval(interval);
              reject(new Error(result || "Task failed on backend"));
            }
            // If PENDING, we just let the interval continue
          } catch (error) {
            clearInterval(interval);
            console.error('Error polling task status:', error);
            reject(error);
          }
        }, pollInterval);
      });
    },
    async sendMessage() {
      const content = this.userInput.trim();
      const promptObj = this.selectedPrompt ? this.prompts.find(p => p.id === this.selectedPrompt) : null;
      let doSummary = promptObj?.name === 'Zusammenfassung von Artikel' && this.summaryFiles.length > 0;
      const withFiles = this.summaryFiles.length > 0;

      if (!content && !doSummary) return;

      const displayContent = doSummary && !content ? `Fass die Datei: ${this.summaryFiles[0].name} zusammen!` : content;
      const sanitized = this.sanitizeContent(displayContent);

      this.currentChatThread.content.push({ role: 'user', content: sanitized });
      this.currentChatThread.send_messages.push({ role: 'user', content: sanitized });

      this.userInput = '';
      this.isLoading = true;
      this.scrollToBottom();

      try {
        if (doSummary) {
          const response = await this.sendWithFiles();
          const taskResult = await this.pollTaskStatus(response.data.task_id);
          this.showTextMessage(taskResult.content, taskResult.sources || []);
        } else {
          if (withFiles) {
            const response = await this.sendContextFiles();
            console.log(response);
            if (response.status > 300) throw new Error('File upload failed');
            this.currentChatThread.id = response.data.chat_id;
          }
          const payload = {
            messages: this.currentChatThread.send_messages,
            system_prompt_id: this.selectedPrompt,
            check_pii: this.anonym,
            chat_id: this.currentChatThread.id,
            language: this.language,
            deep_search: this.deepSearch,
            search_internet: this.searchInternet,
            check_books: this.checkBooks,
            reasoning: this.reasoning,
            debate: this.debate,
            generate_image: this.generateImage,
            search_result: promptObj?.name === 'Recherchieren und zusammenfassen'
          };

          // USE FETCH FOR STREAMING: Axios has poor browser support for getReader()
          const response = await fetch(`${baseURL}/todos/v1/chat/`, {
            method: 'POST',
            headers: {
              'Authorization': 'Token ' + this.token,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          if (!response.ok) throw new Error('Network response was not ok');

          const contentType = response.headers.get('content-type');
          console.log(contentType);
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            this.showTextMessage(data.content, data.sources || []);
          } else if (contentType && contentType.includes('text/html')) {
            const data = await response.text();
            this.showTextMessage(data);
            if (this.generateImage) this.generateImage = false;
          } else {
            // This is where getReader() lives!
            await this.handleStream(response.body);
          }
        }
        await this.finalizeChat();
      } catch (error) {
        console.error('Chat Error:', error);
        this.isLoading = false;
      }
    },

    async finalizeChat() {
      const messages = this.currentChatThread.send_messages;

      // Update Title if it's the start of the conversation
      if (messages.length <= 3) {
        this.currentChatThread.title = await summarizeMessages(messages, 10);
      }

      // Optimize LLM Context (Summarize history if too long)
      if (messages.length > this.maxChatMessages || this.countWords(messages) > this.maxChatWords) {
        const summary = await summarizeMessages(messages);
        this.currentChatThread.send_messages = [{ role: 'system', content: `Previous conversation summary: ${summary}` }];
      }

      this.summaryFiles = [];
      this.youtubeURL = ''; // Clear the URL after sending
      this.$emit('chatCompleted', this.currentChatThread);
    },
    countWords(messages) {
      let wordCounts = 0;

      for (const [key, value] of Object.entries(messages)) {
        if (typeof value === 'string') {
          // Split by whitespace and filter out empty strings
          const words = value.trim().split(/\s+/).filter(word => word.length > 0);
          wordCounts += words.length;
        }
      }

      return wordCounts;
    },
    showTextMessage(message) {
      const md = new markdownit({
        html: true,
        linkify: true, // Optional: auto-convert URL-like text to links
        typographer: true // Optional: beautify quotes and dashes
      });
      this.isLoading = false;
      const html = md.render(message);
      if (this.currentChatThread.content.at(-1).role !== 'assistant') this.currentChatThread.content.push({ role: 'assistant', content: html });
      else this.currentChatThread.content[this.currentChatThread.content.length - 1].content = html
    },
    async handleStream(stream) {
      const md = new markdownit({
        html: true,
        linkify: true, // Optional: auto-convert URL-like text to links
        typographer: true // Optional: beautify quotes and dashes
      });
      const reader = stream.getReader();
      const decoder = new TextDecoder();

      // 1. Initialize empty message
      this.currentChatThread.content.push({
        role: 'assistant',
        content: '',
        sources: [],
        research_logs: '' // Optional: to show deep search progress
      });
      const messageIndex = this.currentChatThread.content.length - 1;

      let buffer = '';
      let showAnswerText = '';
      let fullAnswerText = '';
      let lastStatus = '';
      let sources = []

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          // 2. Decode and append to buffer
          buffer += decoder.decode(value, { stream: true });

          // 3. Process complete SSE messages (separated by \n\n)
          const lines = buffer.split('\n\n');

          // Keep the last part in the buffer (it might be incomplete)
          buffer = lines.pop();

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                // 4. Parse the JSON payload
                const jsonStr = line.substring(6); // Remove "data: "
                const data = JSON.parse(jsonStr);

                // 5. Handle specific event types from your Backend
                if (data.type === 'answer' || data.type === 'status' || data.type === 'research_stream') {
                  // Append standard answer text
                  if (data.content) {
                    if (data.type !== lastStatus && showAnswerText) showAnswerText += ' / ';
                    showAnswerText += data.content;
                    if (data.type === 'answer') fullAnswerText += data.content;
                  }
                  // Reactively update Vue
                  lastStatus = data.type;
                  this.$set(this.currentChatThread.content[messageIndex], 'content', md.render(showAnswerText));
                  this.scrollToBottom();

                } else if (data.type === 'sources') {
                  // Handle sources array
                  sources.push(...data.data);
                  this.$set(this.currentChatThread.content[messageIndex], 'sources', sources);

                } else if (data.type === 'error') {
                  console.error("Backend Error:", data.content);
                }

              } catch (e) {
                console.warn("Failed to parse SSE JSON:", e);
              }
            }
          }
        }
      } catch (err) {
        console.error("Stream processing error", err);
      }

      // Finalize
      fullAnswerText = fullAnswerText.replace(/(?<=\|\s*)\\n(?=\s*\|)/g, '\n');
      this.currentChatThread.send_messages.push({ role: 'assistant', content: fullAnswerText });
      this.$set(this.currentChatThread.content[messageIndex], 'content', md.render(fullAnswerText));
      this.isLoading = false;
      if (this.deepSearch) this.deepSearch = false;
      if (this.debate) this.debate = false;
      return fullAnswerText;
    },
    scrollHandle() {},
    toggleSources(index) {
      this.$set(this.expandedSources, index, !this.expandedSources[index]);
    },
    toggleSourceDetail(msgIndex, sIndex) {
      const key = `${msgIndex}-${sIndex}`;
      this.$set(this.expandedDetails, key, !this.expandedDetails[key]);
    },
    getFavicon(url) {
      if (!url) return '';
      try {
        const domain = new URL(url).hostname;
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
      } catch (e) {
        return '';
      }
    },
    handleIconError(e) {
      e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVR42mP8/5+hH4GZgKpg1IDBA6YGMBIEvPr/n2E6A7mAnm7A6AFTAyY0UHAfAAA/7S/xP899AAAAAElFTkSuQmCC'; // Fallback generic icon
    },
    toggleSidebar() {
      this.$emit('toggleSidebar');
    }
  },
};
</script>

<style scoped>
@-webkit-keyframes wave {
  0%,100%,60% {
    -webkit-transform: initial;
    transform: initial;
  }
  30% {
    -webkit-transform: translateY(-5px);
    transform: translateY(-5px);
  }
}
@keyframes wave {
  0%,100%,60% {
    -webkit-transform: initial;
    transform: initial;
  }
  30% {
    -webkit-transform: translateY(-5px);
    transform: translateY(-5px);
  }
}
.chatbot-container {
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
  border-radius: 8px;
  width: 85%;
  margin: 0 auto;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  height: 80vh;
}

@media (max-width: 767px) {
  .chatbot-container {
    width: 100%;      /* Hide the text label */
  }
}

.ps-container {
  flex: 1; /* Take up all remaining space between header and input */
  position: relative; /* Required for PS */
  overflow: hidden; /* Hide native scrollbar, PS handles it */
}

.chatbot-header {
  background-color: #007bff;
  color: white;
  padding: 10px;
  text-align: center;
  flex-shrink: 0; /* prevent header from shrinking */
}

.chatbot-messages {
  padding: 10px;
  background-color: #f9f9f9;
  min-height: 50vh;
}

.message {
  margin-bottom: 10px;
}

.user-message {
  text-align: left;
  background-color: #e1f5fe;
  padding: 10px 50px 10px 10px;
  border-radius: 10px;
  max-width: 100%;
  align-self: flex-start;
}

.bot-message {
  text-align: left;
  background-color: #f1f1f1;
  padding: 10px 10px 10px 50px;
  border-radius: 10px;
  max-width: 100%;
  align-self: flex-end;
}

/* Inside the purple border: left block (controls+textarea) + right send button */
.input-main-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
}

/* Left section grows and stacks content vertically */
.input-main-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Keep border & pill shape from your original input-container */
.input-container {
  display: flex;
  align-items: stretch; /* so textarea fills vertical height */
  border: 2px solid #9c27b0;
  border-radius: 25px;
  padding: 5px 5px 5px 10px;
  width: 100%;
  box-sizing: border-box;
}

/* Textarea uses all remaining width vertically */
.input-main-left textarea {
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  padding: 10px;
  border: none;
  outline: none;
  border-radius: 18px; /* slightly smaller to fit inside outer pill */
  resize: none;
}

.input-container {
  display: flex;
  align-items: center;
  border: 2px solid #9c27b0;
  border-radius: 25px;
  padding: 5px;
  width: 100%;
}

textarea {
  flex: 1;
  padding: 10px;
  border: none;
  outline: none;
  border-radius: 25px;
}


.rounded-pill {
  border-radius: 50%!important;
  width: 2.5rem;
  height: 2.5rem;
}

button img {
  width: 20px;
  height: 20px;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.copy-button {
  position: fixed;
  border: none;
  cursor: pointer;
}

.chatbot-messages:hover + .copy-button {
  display: inline-block;
}


.attachment-button, .send-button {
  border: none;
  cursor: pointer;
  font-size: 20px;
  margin: 0 5px;
}

.attachment-button {
  background-color: #f9f9f9;
  color: #9c27b0;
}

.attachment-dropdown {
  position: absolute;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  bottom: 50px;
  padding: 5px;
  border: 1px solid #ccc;
  z-index: 1000;
  height: 230px;
}

.attachment-dropdown button {
  display: block;
  padding: 10px;
  width: 100%;
  border: none;
  text-align: left;
  cursor: pointer;
}

.copy-alert {
  position: fixed;
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px;
  border: 1px solid #f5c6cb;
  border-radius: 5px;
  opacity: 1;
  transition: opacity 0.5s ease-out;
}
.copy-alert.fade-out {
  opacity: 0;
}
.mr-0-2 {
  margin-right: 0.3rem;
}
.chat-avatar {
  padding: 8px 15px 0 8px;
  display: flex;
  display: -webkit-flex;
  align-items: flex-start;
  -webkit-align-items: flex-start;
  flex-shrink: 0;
}
.dreams_chat {
  width: 40px;
  height: 40px;
  object-fit: cover;
}
.chats {
  display: flex;
}
/* Each "row" (prompt row, toggles row) left aligned */
.top-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 4px;
  gap: 8px;
}

/* Prompt selector re-aligned for top row */
.prompt-choice {
  min-width: 200px;
  margin-right: 0;
  margin-bottom: 0;
}
.animate-typing-col {
  color: #0D9B35;
  .dot {
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    margin-right: -1px;
    background: #0D9B35;
    -webkit-animation: wave 1.3s linear infinite;
    animation: wave 1.3s linear infinite;
    opacity: .6;
    &:nth-child(2) {
      -webkit-animation-delay: -1.1s;
      animation-delay: -1.1s;
    }
    &:nth-child(3) {
      -webkit-animation-delay: -.9s;
      animation-delay: -.9s;
    }
  }
}

.remove {
  position: absolute;
  top: 2px;
  right: 6px;
  color: #c00;
  font-weight: bold;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  user-select: none;
}
.remove:hover {
  color: #f00;
}

/* Send button fixed on the right */
.send-button {
  border: none;
  cursor: pointer;
  font-size: 20px;
  margin-left: 5px;
  background-color: #9c27b0;
  color: #ffffff;
}

/* Toggle buttons row: left aligned, wrapping when necessary */
.switches-container.switches-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
}

/* Toggle buttons (from previous answer) styled to match your palette */
.toggle-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px; /* Space between icon and text */
  padding: 6px 14px;
  border: 2px solid #ccc;
  border-radius: 20px;
  background-color: #fff;
  color: #333;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.toggle-button:hover {
  border-color: #9c27b0;
  background-color: #f9f9f9;
}

.toggle-button.active {
  background-color: #9c27b0;
  color: #fff;
  border-color: #9c27b0;
}

.toggle-button:active {
  transform: scale(0.98);
}

/* Mobile transformation */
@media (max-width: 767px) {
  .switches-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
  }

  .toggle-button {
    padding: 10px;      /* Equal padding for a circle */
    width: 40px;        /* Fixed dimensions */
    height: 40px;
    border-radius: 50%; /* Make it circular */
  }

  .button-text {
    display: none;      /* Hide the text label */
  }

  .toggle-button i {
    font-size: 18px;    /* Make icons slightly larger for touch */
    margin: 0;
  }
}

/* File list stays above textarea, full width, left aligned */
.file-list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  gap: 8px;
  margin-bottom: 4px;
  width: 100%;
  max-height: 80px;
  padding: 0 10px 0 0;
}

.file-list .file-item {
  background: #f1f1f1;
  border-radius: 12px;
  padding: 2px 8px;
  white-space: normal;
  word-break: break-all;
  font-size: 0.95em;
  position: relative;
}

/* Input area wrapper (already defined in your CSS; we just ensure it’s column) */
.chatbot-input-area {
  display: flex;
  flex-direction: column;
  padding: 10px;
  background-color: #fff;
  border-top: 1px solid #ccc;
  flex-shrink: 0; /* prevent input from shrinking */
  margin-bottom: 0px;
}

/* Row above the purple input-container: left-aligned controls */
.top-controls-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 8px 12px;
  margin-bottom: 6px;
}

/* Prompt selector styling, adjusted for row layout */
.prompt-choice {
  min-width: 220px;
  margin-right: 8px;
  margin-bottom: 0;
  align-content: center;
}

/* Keep your purple border and pill look but allow inner flex layout */
.input-container {
  display: flex;
  align-items: stretch;
  border: 2px solid #9c27b0;
  border-radius: 25px;
  padding: 5px 5px 5px 10px;
  width: 100%;
  box-sizing: border-box;
}

/* Inside purple border: left = textarea block, right = send button */
.input-main-wrapper {
  display: flex;
  width: 100%;
}

/* Left block (file list + textarea) grows */
.input-main-left {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Textarea fills width */
.input-main-left textarea {
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  padding: 10px;
  border: none;
  outline: none;
  border-radius: 18px;
  resize: none;
}

/* Keep your file-list appearance but ensure full width */
.file-list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  gap: 8px;
  margin-bottom: 4px;
  width: 100%;
  max-height: 80px;
  padding: 0 10px 0 0;
}

/* Send button styling (you already have some) */
.send-button {
  border: none;
  cursor: pointer;
  font-size: 20px;
  margin-left: 5px;
  background-color: #9c27b0;
  color: #ffffff;
}

/* Toggle buttons row: left-aligned, wrapping */
.switches-container.switches-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
}

/* Toggle button styles */
.toggle-button {
  padding: 6px 14px;
  border: 2px solid #ccc;
  border-radius: 20px;
  background-color: #fff;
  color: #333;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
}

.toggle-button:hover {
  border-color: #9c27b0;
  background-color: #f9f9f9;
}

.toggle-button.active {
  background-color: #9c27b0;
  color: #fff;
  border-color: #9c27b0;
}

.toggle-button:active {
  transform: scale(0.98);
}

.sources-wrapper {
  margin-top: 12px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.source-header {
  padding: 8px 12px;
  background-color: #f8f9fa;
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 0.9em;
  color: #9c27b0;
}

.source-header:hover {
  background-color: #f0f0f0;
}

.source-list-container {
  padding: 5px 0;
  border-top: 1px solid #eee;
}

.source-item {
  border-bottom: 1px solid #f0f0f0;
}

.source-item:last-child {
  border-bottom: none;
}

.source-item-title {
  padding: 8px 15px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
}

.source-item-title:hover {
  background-color: #fafafa;
}

.favicon {
  width: 16px;
  height: 16px;
  margin-right: 10px;
  border-radius: 2px;
}

.source-name {
  font-size: 0.85em;
  color: #333;
  font-weight: 500;
}

.source-detail {
  padding: 10px 15px 15px 41px; /* Align with name start */
  background-color: #fcfcfc;
  font-size: 0.8em;
  color: #666;
}

.source-snippet {
  margin-bottom: 8px;
  line-height: 1.4;
  font-style: italic;
}

.source-link {
  color: #007bff;
  text-decoration: none;
  word-break: break-all;
}

.source-link:hover {
  text-decoration: underline;
}

.youtube-url-popup {
  background: #f8f9fa;
  padding: 10px;
  border: 1px solid #9c27b0;
  border-radius: 10px;
  margin-bottom: 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.youtube-url-popup .input-group {
  display: flex;
  gap: 5px;
}

.youtube-url-popup input {
  flex: 1;
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

/* Ensure the file-item for YouTube looks distinct */
.file-item[data-type="youtube"] {
  border-left: 4px solid #ff0000;
}

/* Simple Collapse Transition */
.collapse-enter-active, .collapse-leave-active {
  transition: max-height 0.3s ease-out;
  max-height: 300px;
  overflow: hidden;
}
.collapse-enter, .collapse-leave-to {
  max-height: 0;
}
</style>
